import {
  Component,
  ElementRef,
  computed,
  effect,
  inject,
  input,
  isDevMode,
  output,
  signal,
  viewChild,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { LucideAngularModule } from 'lucide-angular';
import { I18nService } from '../../../../core';
import { AppModalComponent, SelectComponent, type SelectItem, ToastService } from '../../../../shared/ui';
import { TPipe } from '../../../../shared/pipes';
import type {
  BrowserInfoPayload,
  PlatformFeedbackApiType,
  PlatformFeedbackFormKind,
} from '../../models/platform-feedback.models';
import { NotificationsStore } from '../../../notifications/store/notifications.store';
import {
  PlatformFeedbackService,
  isPlatformFeedbackConfigurationError,
} from '../../services/platform-feedback.service';

const MAX_FEEDBACK_IMAGE_BYTES = 10 * 1024 * 1024;

function mapFormKindToApi(kind: PlatformFeedbackFormKind): PlatformFeedbackApiType {
  if (kind === 'error') return 'BugReport';
  if (kind === 'criticism') return 'Criticism';
  if (kind === 'template-request') return 'TemplateRequest';
  return 'Suggestion';
}

function collectBrowserInfo(): BrowserInfoPayload {
  if (typeof navigator === 'undefined' || typeof window === 'undefined') {
    return { userAgent: '', language: '', viewportWidth: 0, viewportHeight: 0 };
  }
  return {
    userAgent: navigator.userAgent ?? '',
    language: navigator.language ?? '',
    viewportWidth: window.innerWidth,
    viewportHeight: window.innerHeight,
  };
}

function isAcceptedFeedbackImage(file: File): boolean {
  if (file.size > MAX_FEEDBACK_IMAGE_BYTES) return false;
  const byMime = /^image\/(png|jpeg|webp)$/i.test(file.type);
  const byName = /\.(png|jpe?g|webp)$/i.test(file.name);
  return byMime || byName;
}

@Component({
  selector: 'app-platform-feedback-modal',
  standalone: true,
  imports: [FormsModule, LucideAngularModule, AppModalComponent, SelectComponent, TPipe],
  template: `
    <app-modal
      [visible]="visible()"
      size="md"
      [title]="'platformFeedback.modalTitle' | t"
      [subtitle]="'platformFeedback.modalSubtitle' | t"
      [closeOnOverlay]="!submitting() && !attachmentUploading()"
      (closed)="onDismiss()"
    >
      <div modalBody class="pf-body">
        <label class="field">
          <span class="field-label">{{ 'platformFeedback.fieldType' | t }}</span>
          <app-select
            [label]="''"
            [options]="typeOptions()"
            [value]="kind()"
            (valueChange)="onKindChange($event)"
            [placeholder]="'platformFeedback.typePlaceholder' | t"
            [required]="true"
          />
          @if (showErrors() && !kind()) {
            <span class="field-error">{{ 'platformFeedback.errorType' | t }}</span>
          }
        </label>

        <label class="field">
          <span class="field-label">{{ 'platformFeedback.fieldTitle' | t }}</span>
          <input
            type="text"
            class="text-input"
            [placeholder]="'platformFeedback.titlePlaceholder' | t"
            [(ngModel)]="titleModel"
            [disabled]="submitting() || attachmentUploading()"
            maxlength="200"
            autocomplete="off"
          />
          @if (showErrors() && !titleModel.trim()) {
            <span class="field-error">{{ 'platformFeedback.errorTitle' | t }}</span>
          }
        </label>

        <label class="field">
          <span class="field-label">{{ 'platformFeedback.fieldDescription' | t }}</span>
          <textarea
            class="text-area"
            rows="6"
            [placeholder]="'platformFeedback.descriptionPlaceholder' | t"
            [(ngModel)]="descriptionModel"
            [disabled]="submitting() || attachmentUploading()"
            maxlength="8000"
          ></textarea>
          @if (showErrors() && !descriptionModel.trim()) {
            <span class="field-error">{{ 'platformFeedback.errorDescription' | t }}</span>
          }
        </label>

        <div class="field">
          <span class="field-label">{{ 'platformFeedback.fieldAttachment' | t }}</span>
          <p class="field-hint">{{ 'platformFeedback.attachmentHint' | t }}</p>

          <input
            #attachmentInput
            type="file"
            class="sr-only"
            accept=".png,.jpg,.jpeg,.webp,image/png,image/jpeg,image/webp"
            [disabled]="submitting() || attachmentUploading()"
            (change)="onAttachmentPicked($event)"
          />

          @if (!attachmentPreviewUrl()) {
            <button
              type="button"
              class="btn-attach"
              [disabled]="submitting() || attachmentUploading()"
              (click)="openAttachmentPicker()"
            >
              {{ 'platformFeedback.addImage' | t }}
            </button>
          } @else {
            <div class="attachment-preview">
              @if (attachmentUploading()) {
                <div class="attachment-loading">
                  <lucide-icon name="loader-circle" class="h-6 w-6 spin" aria-hidden="true"></lucide-icon>
                </div>
              }
              <img [src]="attachmentPreviewUrl()!" alt="" class="preview-img" [class.is-hidden]="attachmentUploading()" />
              <button
                type="button"
                class="btn-remove-attach"
                [disabled]="attachmentUploading() || submitting()"
                (click)="clearAttachment()"
              >
                {{ 'platformFeedback.removeImage' | t }}
              </button>
            </div>
          }
          @if (attachmentError()) {
            <span class="field-error">{{ attachmentError() }}</span>
          }
        </div>

        <div class="field context-field">
          <span class="field-label">{{ 'platformFeedback.currentUrlLabel' | t }}</span>
          <input type="text" class="text-input text-input-readonly" [value]="currentUrlDisplay()" readonly />
        </div>
      </div>

      <div modalFooter class="pf-footer">
        <button
          type="button"
          class="btn-secondary"
          [disabled]="submitting() || attachmentUploading()"
          (click)="onDismiss()"
        >
          {{ 'platformFeedback.cancel' | t }}
        </button>
        <button
          type="button"
          class="btn-primary"
          [disabled]="submitting() || attachmentUploading() || !isValid()"
          (click)="onSubmit()"
        >
          @if (submitting()) {
            <lucide-icon name="loader-circle" class="h-4 w-4 spin" aria-hidden="true"></lucide-icon>
            <span>{{ 'platformFeedback.sending' | t }}</span>
          } @else {
            <span>{{ 'platformFeedback.submit' | t }}</span>
          }
        </button>
      </div>
    </app-modal>
  `,
  styles: [
    `
      .pf-body {
        display: grid;
        gap: 14px;
      }

      .sr-only {
        position: absolute;
        width: 1px;
        height: 1px;
        padding: 0;
        margin: -1px;
        overflow: hidden;
        clip: rect(0, 0, 0, 0);
        white-space: nowrap;
        border: 0;
      }

      .field {
        display: grid;
        gap: 4px;
      }

      .field-label {
        font-size: 12px;
        font-weight: 600;
        color: var(--text);
      }

      .field-hint {
        margin: 0 0 4px;
        font-size: 11px;
        color: var(--muted);
      }

      .text-input,
      .text-area {
        width: 100%;
        border: 1px solid var(--border);
        border-radius: 10px;
        background: var(--bg-surface);
        color: var(--text);
        font-size: 13px;
        padding: 8px 10px;
        outline: none;
      }

      .text-input:disabled,
      .text-area:disabled {
        opacity: 0.65;
      }

      .text-input-readonly {
        background: var(--surface-elevated);
        color: var(--muted);
        font-size: 12px;
      }

      .text-area {
        resize: vertical;
        min-height: 120px;
        line-height: 1.45;
      }

      .context-field {
        padding-top: 4px;
      }

      .field-error {
        font-size: 12px;
        color: #b91c1c;
      }

      .btn-attach {
        justify-self: start;
        height: 36px;
        padding: 0 14px;
        border-radius: 10px;
        border: 1px dashed var(--border);
        background: var(--surface-elevated);
        color: var(--text);
        font-size: 13px;
        font-weight: 600;
        cursor: pointer;
      }

      .btn-attach:hover:not(:disabled) {
        background: var(--hover);
        border-color: color-mix(in srgb, var(--border) 70%, var(--text));
      }

      .btn-attach:disabled {
        opacity: 0.5;
        cursor: not-allowed;
      }

      .attachment-preview {
        display: grid;
        gap: 8px;
        max-width: 100%;
      }

      .attachment-loading {
        display: flex;
        align-items: center;
        justify-content: center;
        min-height: 120px;
        border: 1px solid var(--border);
        border-radius: 10px;
        background: var(--surface-elevated);
        color: var(--muted);
      }

      .preview-img {
        max-height: 180px;
        width: auto;
        max-width: 100%;
        border-radius: 10px;
        border: 1px solid var(--border);
        object-fit: contain;
        background: var(--surface-elevated);
      }

      .preview-img.is-hidden {
        display: none;
      }

      .btn-remove-attach {
        justify-self: start;
        height: 32px;
        padding: 0 12px;
        border-radius: 8px;
        border: 1px solid var(--border);
        background: transparent;
        color: var(--muted);
        font-size: 12px;
        font-weight: 600;
        cursor: pointer;
      }

      .btn-remove-attach:hover:not(:disabled) {
        background: var(--hover);
        color: var(--text);
      }

      .btn-remove-attach:disabled {
        opacity: 0.45;
        cursor: not-allowed;
      }

      .pf-footer {
        display: flex;
        justify-content: flex-end;
        align-items: center;
        gap: 8px;
        width: 100%;
      }

      .btn-primary {
        height: 36px;
        border-radius: 10px;
        border: 1px solid #111827;
        background: #111827;
        color: #fff;
        padding: 0 14px;
        font-size: 13px;
        font-weight: 600;
        display: inline-flex;
        align-items: center;
        gap: 8px;
        cursor: pointer;
      }

      .btn-primary:hover:not(:disabled) {
        border-color: #1f2937;
        background: #1f2937;
      }

      .btn-primary:disabled {
        opacity: 0.5;
        cursor: not-allowed;
      }

      .btn-secondary {
        height: 36px;
        border-radius: 10px;
        border: 1px solid var(--border);
        background: var(--surface-elevated);
        color: var(--text);
        padding: 0 14px;
        font-size: 13px;
        font-weight: 600;
        cursor: pointer;
      }

      .btn-secondary:hover:not(:disabled) {
        background: var(--hover);
      }

      .btn-secondary:disabled {
        opacity: 0.5;
        cursor: not-allowed;
      }

      .spin {
        animation: pf-spin 0.9s linear infinite;
      }

      @keyframes pf-spin {
        to {
          transform: rotate(360deg);
        }
      }
    `,
  ],
})
export class PlatformFeedbackModalComponent {
  private readonly api = inject(PlatformFeedbackService);
  private readonly toast = inject(ToastService);
  private readonly router = inject(Router);
  private readonly i18n = inject(I18nService);
  private readonly notificationsStore = inject(NotificationsStore);

  private readonly attachmentInput = viewChild<ElementRef<HTMLInputElement>>('attachmentInput');

  /** Controlado pelo widget pai. */
  readonly visible = input(false);
  /** Quando informado, abre o modal já com o tipo selecionado (sobrescreve o reset). */
  readonly initialKind = input<PlatformFeedbackFormKind | ''>('');
  /** Quando informado, abre o modal com o título já preenchido. */
  readonly initialTitle = input<string>('');
  /** Quando informado, abre o modal com a descrição já preenchida (template editável). */
  readonly initialDescription = input<string>('');
  readonly dismissed = output<void>();

  readonly kind = signal<PlatformFeedbackFormKind | ''>('');
  titleModel = '';
  descriptionModel = '';

  readonly submitting = signal(false);
  readonly attempt = signal(false);

  readonly attachmentId = signal<string | null>(null);
  readonly attachmentPreviewUrl = signal<string | null>(null);
  readonly attachmentUploading = signal(false);
  readonly attachmentError = signal<string | null>(null);

  readonly typeOptions = computed<SelectItem[]>(() => {
    void this.i18n.locale();
    return [
      { value: 'error', label: this.i18n.t('platformFeedback.typeError', undefined, 'Erro') },
      { value: 'suggestion', label: this.i18n.t('platformFeedback.typeSuggestion', undefined, 'Sugestão') },
      { value: 'criticism', label: this.i18n.t('platformFeedback.typeCriticism', undefined, 'Crítica') },
      {
        value: 'template-request',
        label: this.i18n.t(
          'platformFeedback.typeTemplateRequest',
          undefined,
          'Solicitação de template'
        ),
      },
    ];
  });

  readonly currentUrlDisplay = signal('');

  constructor() {
    effect(() => {
      if (!this.visible()) return;
      this.resetForm();
      const initialKind = this.initialKind();
      if (initialKind) this.kind.set(initialKind);
      const initialTitle = this.initialTitle();
      if (initialTitle) this.titleModel = initialTitle;
      const initialDescription = this.initialDescription();
      if (initialDescription) this.descriptionModel = initialDescription;
    });
  }

  showErrors(): boolean {
    return this.attempt();
  }

  isValid(): boolean {
    return !!this.kind() && !!this.titleModel.trim() && !!this.descriptionModel.trim();
  }

  onKindChange(value: string | string[] | null): void {
    const v = Array.isArray(value) ? value[0] : value;
    if (v === 'error' || v === 'suggestion' || v === 'criticism' || v === 'template-request') {
      this.kind.set(v);
    } else {
      this.kind.set('');
    }
  }

  openAttachmentPicker(): void {
    this.attachmentError.set(null);
    this.attachmentInput()?.nativeElement.click();
  }

  async onAttachmentPicked(event: Event): Promise<void> {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];
    input.value = '';
    if (!file) return;

    this.attachmentError.set(null);

    if (!isAcceptedFeedbackImage(file)) {
      this.attachmentError.set(
        this.i18n.t('platformFeedback.invalidImage', undefined, 'Use PNG, JPG, JPEG ou WebP (máx. 10 MB).')
      );
      return;
    }

    this.revokePreviewUrl();
    const preview = URL.createObjectURL(file);
    this.attachmentPreviewUrl.set(preview);
    this.attachmentId.set(null);
    this.attachmentUploading.set(true);

    try {
      const id = await this.api.uploadImageAttachment(file);
      this.attachmentId.set(id);
    } catch (e) {
      if (isDevMode()) {
        console.error('[platform-feedback] upload', e);
      }
      this.revokePreviewUrl();
      this.attachmentPreviewUrl.set(null);
      this.attachmentId.set(null);
      this.toast.show(
        this.i18n.t('platformFeedback.uploadError', undefined, 'Não foi possível enviar a imagem.'),
        'error'
      );
    } finally {
      this.attachmentUploading.set(false);
    }
  }

  clearAttachment(): void {
    this.revokePreviewUrl();
    this.attachmentPreviewUrl.set(null);
    this.attachmentId.set(null);
    this.attachmentError.set(null);
    const el = this.attachmentInput()?.nativeElement;
    if (el) el.value = '';
  }

  onDismiss(): void {
    if (this.submitting() || this.attachmentUploading()) {
      return;
    }
    this.resetForm();
    this.dismissed.emit();
  }

  async onSubmit(): Promise<void> {
    this.attempt.set(true);
    if (!this.isValid()) return;
    const k = this.kind();
    if (k !== 'error' && k !== 'suggestion' && k !== 'criticism') return;

    this.submitting.set(true);
    const browser = collectBrowserInfo();
    const href =
      typeof globalThis.location !== 'undefined' ? globalThis.location.href : this.router.url;

    const ids = this.attachmentId() ? [this.attachmentId()!] : [];

    const payload = {
      type: mapFormKindToApi(k),
      title: this.titleModel.trim(),
      description: this.descriptionModel.trim(),
      currentUrl: href,
      browserInfoJson: JSON.stringify(browser),
      attachmentIds: ids,
      metadataJson: JSON.stringify({ submittedAt: new Date().toISOString() }),
    };

    try {
      await this.api.submit(payload);
      this.toast.success(this.i18n.t('platformFeedback.successToast', undefined, 'Feedback enviado com sucesso.'));
      void this.notificationsStore.loadUnreadCount();
      this.resetForm();
      this.dismissed.emit();
    } catch (e) {
      if (isDevMode()) {
        console.error('[platform-feedback]', e);
      }
      const friendly = this.i18n.t(
        'platformFeedback.errorToast',
        undefined,
        'Não foi possível enviar seu feedback.'
      );
      this.toast.show(friendly, 'error');
      if (isPlatformFeedbackConfigurationError(e) && isDevMode()) {
        console.warn('[platform-feedback] possível erro de configuração no backend');
      }
    } finally {
      this.submitting.set(false);
    }
  }

  private revokePreviewUrl(): void {
    const url = this.attachmentPreviewUrl();
    if (url?.startsWith('blob:')) {
      URL.revokeObjectURL(url);
    }
  }

  private refreshContextUrl(): void {
    const href =
      typeof globalThis.location !== 'undefined' ? globalThis.location.href : this.router.url;
    this.currentUrlDisplay.set(href);
  }

  private resetForm(): void {
    this.revokePreviewUrl();
    this.kind.set('');
    this.titleModel = '';
    this.descriptionModel = '';
    this.attempt.set(false);
    this.submitting.set(false);
    this.attachmentId.set(null);
    this.attachmentPreviewUrl.set(null);
    this.attachmentUploading.set(false);
    this.attachmentError.set(null);
    const el = this.attachmentInput()?.nativeElement;
    if (el) el.value = '';
    this.refreshContextUrl();
  }
}
